import React, { useEffect, useState } from 'react';
import AddressAutocomplete from './AddressAutocomplete';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import GardenModal from './GardenModal';
import { properties, apiFetch } from '../services/api';
import './Settings.css';

const API_URL = process.env.REACT_APP_API_URL;

function Settings() {
  const navigate = useNavigate();
  const [modal, setModal] = useState({ isOpen: false });
  const [userProperties, setUserProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProperty, setNewProperty] = useState({
    name: '',
    address: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: null,
    longitude: null
  });
  const [editingProperty, setEditingProperty] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const response = await properties.getAll();
      setUserProperties(response.data.properties);
      setLoading(false);
    } catch (error) {
      console.error('Error loading properties:', error);
      setLoading(false);
    }
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    if (!newProperty.address) {
      setModal({ isOpen: true, type: "alert", title: "Address Required", message: "Please enter a property address. This is required for weather and zone detection.", onConfirm: () => setModal({ isOpen: false }) });
      return;
    }
    try {
      await properties.create(newProperty);
      setNewProperty({
        name: '',
        address: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        latitude: null,
        longitude: null
      });
      setShowAddForm(false);
      loadProperties();
      setModal({ isOpen: true, type: 'alert', title: 'Success', message: 'Property created successfully', onConfirm: () => { setModal({ isOpen: false }); } });
    } catch (error) {
      console.error('Error creating property:', error);
      setModal({ isOpen: true, type: 'alert', title: 'Error', message: 'Failed to create property', onConfirm: () => setModal({ isOpen: false }) });
    }
  };

  const handleDeleteProperty = async (id) => {
    setModal({
      isOpen: true, type: 'confirm', title: 'Delete Property',
      message: 'Are you sure? This will delete all plants and beds in this property.',
      onConfirm: async () => {
        setModal({ isOpen: false });
        try {
          await properties.delete(id);
          loadProperties();
          setModal({ isOpen: true, type: 'alert', title: 'Deleted', message: 'Property deleted', onConfirm: () => setModal({ isOpen: false }) });
        } catch (error) {
          console.error('Error deleting property:', error);
          setModal({ isOpen: true, type: 'alert', title: 'Error', message: 'Failed to delete property', onConfirm: () => setModal({ isOpen: false }) });
        }
      },
      onCancel: () => setModal({ isOpen: false })
    });
  };

  const handleEditProperty = async (e) => {
    e.preventDefault();
    if (!editForm.address) {
      setModal({ isOpen: true, type: "alert", title: "Address Required", message: "Please enter a property address. This is required for weather and zone detection.", onConfirm: () => setModal({ isOpen: false }) });
      return;
    }
    try {
      await apiFetch(`${API_URL}/properties/${editingProperty}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      setEditingProperty(null);
      loadProperties();
      setModal({ isOpen: true, type: 'alert', title: 'Success', message: 'Property updated', onConfirm: () => { setModal({ isOpen: false }); setEditingProperty(null); } });
    } catch (error) {
      console.error('Error updating property:', error);
      setModal({ isOpen: true, type: 'alert', title: 'Error', message: 'Failed to update property', onConfirm: () => setModal({ isOpen: false }) });
    }
  };

  if (loading) {
    return <div className="settings-loading">Loading settings...</div>;
  }

  return (
    <div className={`settings-container${isMobile ? ' settings-container--mobile' : ''}`}>
      <h2>Properties</h2>
      <GardenModal isOpen={modal.isOpen} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} onCancel={() => setModal({ isOpen: false })} />

      <div className="settings-section">
        <div className="settings-header">
          <h3>My Properties</h3>
          {!showAddForm && (
            <button className="settings-add-btn" onClick={() => setShowAddForm(true)}>
              + Add Property
            </button>
          )}
        </div>

        {showAddForm && (
          <form onSubmit={handleCreateProperty} className="settings-add-form">
            <h4>New Property</h4>

            <div className="settings-field">
              <label className="settings-label">Property Name *</label>
              <input
                className="settings-input"
                type="text"
                value={newProperty.name}
                onChange={(e) => setNewProperty({...newProperty, name: e.target.value})}
                required
                placeholder="e.g., My Garden, Backyard"
              />
            </div>

            <div className="settings-field">
              <label className="settings-label">Address *</label>
              <AddressAutocomplete
                onAddressSelect={(addressData) => {
                  setNewProperty({
                    ...newProperty,
                    address: addressData.fullAddress,
                    streetAddress: addressData.streetAddress,
                    city: addressData.city,
                    state: addressData.state,
                    zipCode: addressData.zipCode,
                    latitude: addressData.latitude,
                    longitude: addressData.longitude
                  });
                }}
              />
              <p className="settings-hint">Type your address and select from the dropdown</p>
            </div>

            <div className="settings-form-actions">
              <button type="submit" className="settings-btn-primary">Create Property</button>
              <button
                type="button"
                className="settings-btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setNewProperty({ name: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {userProperties.length === 0 && !showAddForm && (
          <div className="settings-empty">
            <p>No properties yet. Create one to get started!</p>
          </div>
        )}

        {userProperties.map(property => (
          <React.Fragment key={property.id}>
            <div className="settings-property-card">
              <div className="settings-property-inner">
                <div>
                  <h4>{property.name}</h4>
                  {property.address && (
                    <p className="settings-property-address">{property.address}</p>
                  )}
                  {property.usda_zone && (
                    <p className="settings-property-zone">Hardiness Zone: {property.usda_zone}</p>
                  )}
                  <p className="settings-property-tz">Timezone: {property.timezone}</p>
                </div>
                <div className="settings-property-actions">
                  {property.usda_zone && (
                    <button
                      className="settings-btn-generate"
                      onClick={async () => {
                        try {
                          const response = await apiFetch(`${process.env.REACT_APP_API_URL}/tasks/generate-lawn-care/${property.id}`, {
                            method: 'POST'
                          });
                          const data = await response.json();
                          if (response.ok) {
                            setModal({ isOpen: true, type: 'alert', message: data.message, onConfirm: () => { setModal({ isOpen: false }); navigate('/tasks'); } });
                          } else {
                            setModal({ isOpen: true, type: 'alert', title: 'Error', message: data.error, onConfirm: () => setModal({ isOpen: false }) });
                          }
                        } catch (error) {
                          console.error('Generate lawn care error:', error);
                          setModal({ isOpen: true, type: 'alert', title: 'Error', message: 'Failed to generate lawn care schedule', onConfirm: () => setModal({ isOpen: false }) });
                        }
                      }}
                    >
                      Generate Lawn Care
                    </button>
                  )}
                  <button className="settings-btn-delete" onClick={() => handleDeleteProperty(property.id)}>
                    Delete
                  </button>
                  <button
                    className="settings-btn-edit"
                    onClick={() => {
                      setEditingProperty(property.id);
                      setEditForm({
                        name: property.name,
                        address: property.address || '',
                        streetAddress: property.street_address || '',
                        city: property.city || '',
                        state: property.state || '',
                        zipCode: property.zip_code || '',
                        latitude: property.latitude || null,
                        longitude: property.longitude || null
                      });
                    }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>

            {editingProperty === property.id && (
              <form onSubmit={handleEditProperty} className="settings-edit-form">
                <h4>Edit Property</h4>
                <div className="settings-field">
                  <label className="settings-label">Property Name</label>
                  <input
                    className="settings-input"
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    required
                  />
                </div>
                <div className="settings-field">
                  <label className="settings-label">Address</label>
                  <AddressAutocomplete
                    initialValue={editForm.address}
                    onAddressSelect={(addressData) => {
                      setEditForm({
                        ...editForm,
                        address: addressData.fullAddress,
                        streetAddress: addressData.streetAddress,
                        city: addressData.city,
                        state: addressData.state,
                        zipCode: addressData.zipCode,
                        latitude: addressData.latitude,
                        longitude: addressData.longitude
                      });
                    }}
                  />
                </div>
                <div className="settings-form-actions">
                  <button type="submit" className="settings-btn-primary">Save</button>
                  <button type="button" className="settings-btn-secondary" onClick={() => setEditingProperty(null)}>Cancel</button>
                </div>
              </form>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default Settings;
